<template>
  <v-container class="py-6">
    <v-card elevation="2" class="mb-4">
      <v-card-title class="d-flex align-center justify-space-between">
        <div class="d-flex align-center gap-3">
          <v-icon color="primary" size="28">mdi-briefcase-chart</v-icon>
          <span class="text-h6">Reportes Ejecutivos</span>
          <v-chip v-if="lastUpdated" size="small" color="grey-200" class="ml-2" variant="tonal">
            Actualizado: {{ new Date(lastUpdated).toLocaleString() }}
          </v-chip>
        </div>
        <!-- The controls are part of the promise: an actor whose producer refuses gets none. -->
        <div v-if="producerServesActor" class="d-flex align-center gap-2">
          <v-btn size="small" variant="tonal" color="primary" :loading="loading" :disabled="!selectedAdministrationId" @click="refresh">
            <v-icon start>mdi-refresh</v-icon>Actualizar
          </v-btn>
          <v-btn size="small" variant="outlined" color="secondary" @click="exportCSV" :disabled="!metrics" data-testid="executive-export-csv">
            <v-icon start>mdi-file-delimited</v-icon>CSV
          </v-btn>
          <v-btn size="small" variant="outlined" color="secondary" @click="printReport" :disabled="!metrics">
            <v-icon start>mdi-printer</v-icon>Imprimir
          </v-btn>
        </div>
      </v-card-title>
      <v-card-subtitle class="px-4 pb-4">
        Incidencias por administración: totales, estados, prioridad y desglose por departamento.
      </v-card-subtitle>
      <!--
        P13-V5A-R5 — este componente se monta también dentro de /reports/realtime, cuya ruta
        admite group_manager; su ÚNICO productor no. Se dice, en vez de mostrar un informe vacío
        que parecería una organización sin actividad.
      -->
      <v-card-text v-if="!producerServesActor" data-testid="executive-reports-role-unavailable">
        <v-alert type="info" variant="tonal" density="compact">
          Los reportes ejecutivos por administración no están disponibles para su rol: su
          productor canónico sólo atiende a super_admin, administration_manager y
          field_supervisor.
        </v-alert>
      </v-card-text>

      <v-card-text v-else>
        <v-row align="center" class="mb-2">
          <v-col cols="12" md="5">
            <!--
              P13-V7B: the options are built from the rows the overview publishes —
              `{ administration: { id, name, code, type }, incidents }` — not from `row.name` /
              `row.id`, which the rows never carried: every option used to be "Admin undefined" with
              no value, and nothing could be selected.
            -->
            <v-select
              v-model="selectedAdministrationId"
              :items="adminOptions"
              :loading="adminOptionsLoading"
              density="comfortable"
              variant="outlined"
              label="Administración"
              item-title="title"
              item-value="value"
              clearable
              hide-details
              no-data-text="No hay administraciones disponibles"
              data-testid="executive-administration-select"
            />
          </v-col>
        </v-row>

        <v-alert
          v-if="optionsError"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-4"
          data-testid="executive-options-error"
        >
          {{ optionsError }}
        </v-alert>

        <v-alert v-if="isGroupBounded" type="info" variant="tonal" density="compact" class="mb-4" data-testid="executive-group-bounded">
          Las cifras se limitan a las incidencias de sus grupos dentro de la administración: el
          servidor acota la lectura a su alcance de supervisión.
        </v-alert>

        <v-alert v-if="!selectedAdministrationId && adminOptions.length > 0" type="info" variant="tonal" class="mb-4">
          Selecciona una administración para visualizar los reportes ejecutivos.
        </v-alert>

        <v-alert
          v-if="metricsError"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-4"
          data-testid="executive-metrics-error"
        >
          No se pudieron leer las métricas de esta administración: {{ metricsError }}. No se muestra ninguna cifra.
        </v-alert>

        <template v-if="metrics">
          <!--
            P13-V7B: the figures are read where `GET /api/administrations/:id/metrics` publishes them
            — `incidents.total`, `incidents.by_status.*`, `incidents.by_priority.*`,
            `departments.breakdown`. The page read `totals.all`,
            `status.open`, `sla.compliance` and `departments`, none of which the producer sends, so
            every card said "—" over a producer that had answered.
            P13-V5A-R5: `stat()` still distinguishes a stated zero (renders 0) from an absent field.
            P13-V8 (DEC-V8-07): no SLA metric is delivered or advertised in the pilot. The "SLA
            Cumplimiento" card (estimated vs real resolution time — a target-vs-observed SLA
            comparison no clause sells) is retired with its producer block; it could only ever say
            "Sin datos".
          -->
          <v-row>
            <v-col v-for="card in statusCards" :key="card.key" cols="12" sm="6" md="2">
              <v-card variant="tonal" :color="card.color">
                <v-card-text>
                  <div class="text-caption text-medium-emphasis">{{ card.label }}</div>
                  <div class="text-h5 font-weight-bold" :data-testid="`executive-kpi-${card.key}`">{{ card.value }}</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row class="mt-1">
            <v-col cols="12">
              <v-card elevation="1">
                <v-card-title class="text-subtitle-1">Incidencias creadas por minuto (últimas 24 h)</v-card-title>
                <v-card-text>
                  <RealtimeSparkline v-if="trendNumbers.length > 0" :points="trendNumbers" :height="64" />
                  <div v-else class="text-body-2 text-medium-emphasis" data-testid="executive-trend-absent">
                    Sin serie temporal en esta lectura.
                  </div>
                  <div class="text-caption text-medium-emphasis mt-2" data-testid="executive-last-24h">
                    Últimas 24 h: {{ stat(metrics.incidents?.last_24h?.created) }} creadas ·
                    {{ stat(metrics.incidents?.last_24h?.closed) }} cerradas
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row class="mt-1">
            <v-col cols="12" md="6">
              <v-card elevation="1">
                <v-card-title class="text-subtitle-1">Por prioridad</v-card-title>
                <v-card-text>
                  <div v-for="row in priorityRows" :key="row.key" class="d-flex justify-space-between py-1">
                    <span>{{ row.label }}</span>
                    <span class="font-weight-medium" :data-testid="`executive-priority-${row.key}`">{{ row.value }}</span>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="6">
              <v-card elevation="1">
                <v-card-title class="text-subtitle-1">Administración</v-card-title>
                <v-card-text>
                  <div class="d-flex justify-space-between py-1">
                    <span>Usuarios</span><span class="font-weight-medium">{{ stat(metrics.users?.total) }}</span>
                  </div>
                  <div class="d-flex justify-space-between py-1">
                    <span>Puntos</span><span class="font-weight-medium">{{ stat(metrics.points?.total) }}</span>
                  </div>
                  <div class="d-flex justify-space-between py-1">
                    <span>Departamentos</span><span class="font-weight-medium">{{ stat(metrics.departments?.total) }}</span>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-card class="mt-4" elevation="1">
            <v-card-title class="text-subtitle-1">Desglose por Departamento</v-card-title>
            <v-data-table
              :headers="deptHeaders"
              :items="deptRows"
              :items-per-page="5"
              density="comfortable"
              class="elevation-0"
              no-data-text="La administración no tiene departamentos."
              data-testid="executive-department-breakdown"
            />
          </v-card>
        </template>

        <v-skeleton-loader v-else-if="loading" type="image, article, table" />
      </v-card-text>
    </v-card>
  </v-container>
  <v-snackbar v-model="snackbar" color="success" timeout="2500">
    {{ snackbarText }}
  </v-snackbar>
  <v-snackbar v-model="errorbar" color="error" timeout="3500">
    {{ errorText }}
  </v-snackbar>

</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import RealtimeSparkline from '@/components/analytics/RealtimeSparkline.vue'
import { useAdminMetricsStore } from '@/stores/admin-metrics'
import { useAuthStore } from '@/stores/auth'

interface Option { title: string; value: number }

const adminStore = useAdminMetricsStore()
const authStore = useAuthStore()

/**
 * ── P13-V5A-R5 · ADVERSARIAL REPAIR · THE PRODUCER'S ADMISSION TRAVELS WITH THE COMPONENT ──
 *
 * R1 (NEW-12) narrowed the `/reports/executive` ROUTE to the roles this component's only producer
 * actually serves — `GET /api/administrations/:id/metrics` admits super_admin,
 * administration_manager and field_supervisor — because a `group_manager` reached a page that
 * could only ever answer 403.
 *
 * That fence was placed on the route, and this component is ALSO embedded, unconditionally, in
 * `views/reports/RealtimeReports.vue:5` — whose route admits `group_manager`. So the capability
 * R1 withdrew came straight back through component composition rather than through a route, and
 * the adversarial pass found it there. Fixing only the embedding site would leave the next
 * embedding to rediscover the same hole, so the admission now lives WITH the component: wherever
 * it is mounted, it states the truth about who its producer serves.
 *
 * This is UX admission, not authorisation. It grants nothing, widens nothing and duplicates no
 * decision — it is R1's own conclusion, applied where the component is rather than where one of
 * its callers is. The backend remains the authority and refuses independently.
 */
const PRODUCER_ROLES = Object.freeze(['super_admin', 'administration_manager', 'field_supervisor'])
const producerServesActor = computed(() => PRODUCER_ROLES.includes(String(authStore.userRole || '')))

/**
 * P13-V7B — WHERE THE ADMINISTRATION LIST COMES FROM.
 *
 * `GET /api/administrations/metrics/overview` admits super_admin and administration_manager only
 * (administrationRoutes.js). The page also admits field_supervisor, whose producer read
 * (`/:id/metrics`) is served but whose overview read could only answer 403 — the selector stayed
 * empty behind an error. For that role the one administration offered is the session's own
 * (`user.administrationId`, reference identity reported by /auth/validate, exactly as the
 * administration_manager home reads it); the server re-derives the partition on every request and
 * bounds a supervisor's figures to its own groups (P13-V5A-R1 NEW-18).
 */
const OVERVIEW_ROLES = Object.freeze(['super_admin', 'administration_manager'])
const readsOverview = computed(() => OVERVIEW_ROLES.includes(String(authStore.userRole || '')))
const isGroupBounded = computed(() => String(authStore.userRole || '') === 'field_supervisor')

const selectedAdministrationId = ref<number | null>(null)
const adminOptions = ref<Option[]>([])
const adminOptionsLoading = ref(false)
const optionsError = ref<string | null>(null)
const metricsError = ref<string | null>(null)
const loading = computed(() => adminStore.loading)
/**
 * Freshness is read from the STORE, so it has the same lifetime as the figures it describes.
 * Held in the component it reset on every mount while the store-global data survived, and
 * re-entering the view republished the previous acquisition with its "Actualizado" chip gone.
 */
const lastUpdated = computed<number | null>(() => {
  const id = selectedAdministrationId.value
  return id ? adminStore.updatedAt[id] ?? null : null
})

const snackbar = ref(false)
const snackbarText = ref('')
const errorbar = ref(false)
const errorText = ref('')

const metrics = computed<any>(() => {
  const id = selectedAdministrationId.value
  return id ? adminStore.data[id] : null
})

/** A stated number renders as itself — zero included. Anything else is an explicit absence. */
const ABSENT_STAT = '—'
const stat = (value: unknown): string => {
  if (value === null || value === undefined) return ABSENT_STAT
  const parsed = Number(value)
  return Number.isFinite(parsed) ? String(parsed) : ABSENT_STAT
}

/** The five canonical statuses the producer counts (`incidents.by_status`), plus the total. */
const statusCards = computed(() => {
  const incidents = metrics.value?.incidents
  const by = incidents?.by_status
  return [
    { key: 'total', label: 'Incidencias Totales', value: stat(incidents?.total), color: 'primary' },
    { key: 'pending', label: 'Pendientes', value: stat(by?.pending), color: 'warning' },
    { key: 'in_progress', label: 'En Progreso', value: stat(by?.in_progress), color: 'info' },
    { key: 'pending_validation', label: 'Pendiente Validación', value: stat(by?.pending_validation), color: 'secondary' },
    { key: 'closed', label: 'Cerradas', value: stat(by?.closed), color: 'success' },
    { key: 'cancelled', label: 'Canceladas', value: stat(by?.cancelled), color: 'grey' }
  ]
})

const PRIORITY_LABELS: Record<string, string> = { low: 'Baja', medium: 'Media', high: 'Alta', critical: 'Crítica' }
const priorityRows = computed(() => {
  const by = metrics.value?.incidents?.by_priority
  return Object.entries(PRIORITY_LABELS).map(([key, label]) => ({ key, label, value: stat(by?.[key]) }))
})

const trendNumbers = computed<number[]>(() => {
  const trend = metrics.value?.trend || []
  if (Array.isArray(trend) && trend.length > 0) {
    if (typeof trend[0] === 'number') return trend as number[]
    // The producer buckets per minute as { t, v }; a point without a stated count is dropped, not zeroed.
    return (trend as any[])
      .map(p => Number(p?.v ?? p?.value))
      .filter(v => Number.isFinite(v))
  }
  return []
})

const deptHeaders = [
  { title: 'Departamento', value: 'name' },
  { title: 'Sin finalizar', value: 'open' },
  { title: 'En Progreso', value: 'in_progress' },
  { title: 'Cerradas', value: 'closed' },
  { title: 'Total', value: 'total' },
]

/**
 * P13-V5A-R5 — the per-department rows used the same `?? 0` substitution, and the `total`
 * column went further: it SYNTHESISED a total by summing three fields that were themselves
 * defaulted to zero, so a department the producer described only partially was published with
 * a computed total it never reported. A total is shown only when the producer stated it.
 *
 * P13-V7B — the rows are `departments.breakdown` (`{ id, name, code, total, open, in_progress,
 * closed }`); `departments` itself is `{ total, breakdown }`, so the table used to be empty. The
 * producer's `open` counts every status not yet finalised (pending, in_progress,
 * pending_validation), which is what the column now says; it overlaps "En Progreso" by
 * definition, so no total is derived from the three columns.
 */
const deptRows = computed(() => {
  const list = Array.isArray(metrics.value?.departments?.breakdown) ? metrics.value.departments.breakdown : []
  return list.map((d: any) => ({
    name: d?.code ? `${d.name} (${d.code})` : (d?.name ?? ABSENT_STAT),
    open: stat(d?.open),
    in_progress: stat(d?.in_progress),
    closed: stat(d?.closed),
    total: stat(d?.total)
  }))
})

function persistSelection(id: number | null) {
  try {
    if (id) localStorage.setItem('selectedAdministrationId', String(id))
    else localStorage.removeItem('selectedAdministrationId')
  } catch {}
}

function refusal(e: any, fallback: string): string {
  const body = e?.response?.data
  if (e?.response?.status === 403) return 'el servidor no le concede esta lectura'
  return (body && (body.message || body.error)) || e?.message || fallback
}

async function loadAdminOptions() {
  adminOptionsLoading.value = true
  optionsError.value = null
  try {
    if (readsOverview.value) {
      const overview = await adminStore.fetchOverview()
      adminOptions.value = (Array.isArray(overview) ? overview : [])
        .map((row: any) => row?.administration)
        .filter((a: any) => a && Number.isInteger(Number(a.id)) && Number(a.id) > 0)
        .map((a: any) => ({ title: a.code ? `${a.name} (${a.code})` : String(a.name ?? `#${a.id}`), value: Number(a.id) }))
        .sort((a: Option, b: Option) => a.title.localeCompare(b.title))
    } else {
      const own = Number((authStore.user as any)?.administrationId)
      adminOptions.value = Number.isInteger(own) && own > 0 ? [{ title: `Su administración (#${own})`, value: own }] : []
      if (adminOptions.value.length === 0) {
        optionsError.value = 'Su sesión no tiene una administración asignada: no hay ninguna que consultar.'
      }
    }
  } catch (e: any) {
    adminOptions.value = []
    optionsError.value = `No se pudo leer la lista de administraciones: ${refusal(e, 'la lectura falló')}.`
  } finally {
    adminOptionsLoading.value = false
  }
}

async function refresh() {
  if (!selectedAdministrationId.value) return
  metricsError.value = null
  try {
    await adminStore.fetchMetrics(selectedAdministrationId.value, { includeTrend: true, windowMinutes: 1440 })
    snackbar.value = true
    snackbarText.value = 'Métricas actualizadas'
  } catch (e: any) {
    metricsError.value = refusal(e, 'la lectura falló')
  }
}

/** CSV cells quoted so a name with a comma or a quote cannot shift the columns. */
const csvCell = (value: unknown): string => `"${String(value ?? '').replace(/"/g, '""')}"`

function exportCSV() {
  if (!metrics.value) return
  // P13-V5A-R5: the export is a business output (§21) and must carry the same truth as the
  // screen — it is built from the SAME computed rows the page renders, so an absence exports as
  // an absence. P13-V7B: those rows now read the producer's real fields.
  const rows: string[] = []
  rows.push(['Sección', 'Clave', 'Valor'].map(csvCell).join(','))
  for (const card of statusCards.value) rows.push(['incidencias', card.key, card.value].map(csvCell).join(','))
  for (const row of priorityRows.value) rows.push(['prioridad', row.key, row.value].map(csvCell).join(','))
  rows.push(['departamento', 'sin_finalizar', 'en_progreso', 'cerradas', 'total'].map(csvCell).join(','))
  deptRows.value.forEach((d) => {
    rows.push([d.name, d.open, d.in_progress, d.closed, d.total].map(csvCell).join(','))
  })
  const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `executive-report-${selectedAdministrationId.value}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * P13-V5A-R5 — the control was labelled "PDF" but no PDF generator exists in this product;
 * it opens the browser print dialog. A button that names a capability the product does not
 * have is a capability claim (§6), so it now says what it does.
 */
function printReport() {
  window.print()
}

onMounted(async () => {
  // P13-V5A-R5: declare the reader BEFORE anything can render. Figures acquired by a previous
  // actor are dropped here, so an in-SPA session turnover cannot publish them to the next one.
  adminStore.claimFor(authStore.user?.id ?? null)

  if (!producerServesActor.value) return

  await loadAdminOptions()
  // P13-V7B: a remembered selection is restored only when it is one of the administrations this
  // session was just offered; any other id would only earn a 403 for a choice nobody made here.
  let saved: number | null = null
  try { saved = Number(localStorage.getItem('selectedAdministrationId')) || null } catch {}
  const offered = adminOptions.value.map(o => o.value)
  if (saved && offered.includes(saved)) selectedAdministrationId.value = saved
  else if (offered.length === 1) selectedAdministrationId.value = offered[0]
})

watch(selectedAdministrationId, async (val) => {
  persistSelection(val)
  metricsError.value = null
  if (val) await refresh()
})
</script>

<style scoped>
.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }
</style>
